/**
 * Schema Extraction Utilities
 * Extracts JSON schema from data objects for template generation
 */

import type { JSONSchema, JSONSchemaProperty } from '../../modules/shared/types/src/lib/template-types';

/**
 * Infer the type of a value
 */
function inferType(value: any): string {
  if (value === null || value === undefined) {
    return 'string';
  }

  if (Array.isArray(value)) {
    return 'array';
  }

  const type = typeof value;

  switch (type) {
    case 'boolean':
      return 'boolean';
    case 'number':
      return 'number';
    case 'object':
      return 'object';
    case 'string':
    default:
      return 'string';
  }
}

/**
 * Infer format for string types based on value patterns
 * Uses ReDoS-safe patterns with bounded quantifiers and string methods
 */
function inferFormat(value: string): string | undefined {
  // Input length limit to prevent ReDoS
  if (!value || value.length > 500) {
    return undefined;
  }

  // Email pattern - Safe: Uses string methods instead of complex regex
  if (value.includes('@') && value.includes('.')) {
    const atIndex = value.indexOf('@');
    const lastDotIndex = value.lastIndexOf('.');
    // Basic validation: @ before last dot, no spaces, reasonable length
    if (atIndex > 0 && 
        lastDotIndex > atIndex + 1 && 
        lastDotIndex < value.length - 1 &&
        !value.includes(' ') &&
        value.length <= 254) { // RFC 5321 max length
    return 'email';
    }
  }

  // ISO Date pattern - Safe: Exact character count with \d{fixed}
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) {
    return 'date-time';
  }

  // Simple date pattern - Safe: Exact character count
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return 'date';
  }

  // URL pattern - Safe: Uses startsWith with bounded length check
  if ((value.startsWith('http://') || value.startsWith('https://')) && 
      value.length > 7 && 
      value.length <= 2048) { // Reasonable URL max length
    return 'uri';
  }

  // Phone pattern - Safe: Uses bounded quantifier {0,30} instead of +
  if (/^\+?[\d\s\-()]{0,30}$/.test(value)) {
    const digitsOnly = value.replace(/\D/g, '');
    if (digitsOnly.length >= 10 && digitsOnly.length <= 15) {
    return 'tel';
    }
  }

  return undefined;
}

/**
 * Generate human-readable label from field name
 */
function generateLabel(fieldName: string): string {
  return fieldName
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
}

/**
 * Generate description based on field name and type
 */
function generateDescription(fieldName: string, type: string, format?: string): string {
  const label = generateLabel(fieldName);

  if (format === 'email') return `Enter ${label} email address`;
  if (format === 'date' || format === 'date-time') return `${label} date`;
  if (format === 'tel') return `${label} phone number`;
  if (format === 'uri') return `${label} URL`;
  if (type === 'boolean') return `${label} flag`;
  if (type === 'number') return `${label} value`;
  if (type === 'array') return `List of ${label.toLowerCase()}`;
  if (type === 'object') return `${label} information`;

  return `${label} field`;
}

/**
 * Extract schema property from a value
 */
function extractSchemaProperty(key: string, value: any, depth: number = 0): JSONSchemaProperty {
  const type = inferType(value);
  const property: JSONSchemaProperty = {
    type,
    description: ''
  };

  if (type === 'string' && typeof value === 'string') {
    const format = inferFormat(value);
    if (format) {
      property.format = format;
    }
    property.description = generateDescription(key, type, format);
  } else if (type === 'array' && Array.isArray(value) && value.length > 0) {
    // Infer array item type from first element
    const firstItem = value[0];
    property.items = extractSchemaProperty('item', firstItem, depth + 1);
    property.description = generateDescription(key, type);
  } else if (type === 'object' && value !== null && depth < 5) {
    // Recursively extract nested object properties (limit depth to prevent infinite recursion)
    property.properties = {};
    for (const [nestedKey, nestedValue] of Object.entries(value)) {
      property.properties[nestedKey] = extractSchemaProperty(nestedKey, nestedValue, depth + 1);
    }
    property.description = generateDescription(key, type);
  } else {
    property.description = generateDescription(key, type);
  }

  // Add default value if it's a primitive
  if (type !== 'object' && type !== 'array' && value !== null && value !== undefined) {
    property.default = value;
  }

  return property;
}

/**
 * Extract complete JSON schema from data object
 */
export function extractClaimDataSchema(data: any): JSONSchema {
  if (!data || typeof data !== 'object') {
    return {
      type: 'object',
      description: 'Empty or invalid data',
      properties: {}
    };
  }

  const schema: JSONSchema = {
    type: 'object',
    description: 'Extracted data schema',
    properties: {},
    required: []
  };

  // Extract properties
  for (const [key, value] of Object.entries(data)) {
    if (value !== null && value !== undefined) {
      schema.properties![key] = extractSchemaProperty(key, value);

      // Mark non-null fields as required
      if (value !== null && value !== undefined && value !== '') {
        schema.required!.push(key);
      }
    }
  }

  return schema;
}

/**
 * Generate sample data from schema (for testing/preview)
 */
export function generateSampleDataFromSchema(schema: JSONSchema): any {
  if (schema.type !== 'object' || !schema.properties) {
    return null;
  }

  const data: any = {};

  for (const [key, property] of Object.entries(schema.properties)) {
    if (property.default !== undefined) {
      data[key] = property.default;
    } else if (property.type === 'string') {
      if (property.format === 'email') {
        data[key] = 'example@email.com';
      } else if (property.format === 'date' || property.format === 'date-time') {
        data[key] = new Date().toISOString();
      } else if (property.format === 'tel') {
        data[key] = '(555) 123-4567';
      } else if (property.format === 'uri') {
        data[key] = 'https://example.com';
      } else {
        data[key] = 'Sample text';
      }
    } else if (property.type === 'number') {
      data[key] = 100;
    } else if (property.type === 'boolean') {
      data[key] = true;
    } else if (property.type === 'array' && property.items) {
      data[key] = [generateSampleDataFromProperty(property.items)];
    } else if (property.type === 'object' && property.properties) {
      data[key] = generateSampleDataFromSchema({ type: 'object', properties: property.properties });
    }
  }

  return data;
}

/**
 * Generate sample data for a single property
 */
function generateSampleDataFromProperty(property: JSONSchemaProperty): any {
  if (property.default !== undefined) {
    return property.default;
  }

  if (property.type === 'string') {
    if (property.format === 'email') return 'example@email.com';
    if (property.format === 'date' || property.format === 'date-time') return new Date().toISOString();
    if (property.format === 'tel') return '(555) 123-4567';
    if (property.format === 'uri') return 'https://example.com';
    return 'Sample';
  }

  if (property.type === 'number') return 100;
  if (property.type === 'boolean') return true;

  if (property.type === 'array' && property.items) {
    return [generateSampleDataFromProperty(property.items)];
  }

  if (property.type === 'object' && property.properties) {
    return generateSampleDataFromSchema({ type: 'object', properties: property.properties });
  }

  return null;
}

/**
 * Validate data against schema (basic validation)
 */
export function validateDataAgainstSchema(data: any, schema: JSONSchema): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  if (schema.type === 'object' && schema.properties) {
    // Check required fields
    if (schema.required) {
      for (const requiredField of schema.required) {
        if (!(requiredField in data)) {
          errors.push(`Required field '${requiredField}' is missing`);
        }
      }
    }

    // Check property types
    for (const [key, property] of Object.entries(schema.properties)) {
      if (key in data) {
        const value = data[key];
        const expectedType = property.type;
        const actualType = inferType(value);

        if (actualType !== expectedType && value !== null && value !== undefined) {
          errors.push(`Field '${key}' expected type '${expectedType}' but got '${actualType}'`);
        }
      }
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Merge multiple schemas (useful for combining different data sources)
 */
export function mergeSchemas(...schemas: JSONSchema[]): JSONSchema {
  const merged: JSONSchema = {
    type: 'object',
    properties: {},
    required: []
  };

  const allRequired = new Set<string>();

  for (const schema of schemas) {
    if (schema.properties) {
      merged.properties = {
        ...merged.properties,
        ...schema.properties
      };
    }

    if (schema.required) {
      schema.required.forEach(field => allRequired.add(field));
    }
  }

  merged.required = Array.from(allRequired);

  return merged;
}

